import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';

function hhmm(ts) {
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function FleetSchedulerPage() {
  const { hasCapability } = useAuth();
  const canWrite = hasCapability('operations_write');

  const [gantt, setGantt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);
  const [dragJobId, setDragJobId] = useState(null);
  const [dropBusyTug, setDropBusyTug] = useState(null);

  const load = useCallback(async () => {
    const res = await api.getSchedulerGantt();
    setGantt(res || null);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load the fleet schedule.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const { hours, windowStartMs, windowMs } = useMemo(() => {
    const sH = gantt?.windowStartHour ?? 6;
    const eH = gantt?.windowEndHour ?? 20;
    const hrs = [];
    for (let h = sH; h <= eH; h += 1) hrs.push(h);
    const base = gantt?.date ? new Date(`${gantt.date}T00:00:00.000Z`).getTime() : 0;
    return {
      hours: hrs,
      windowStartMs: base + sH * 3_600_000,
      windowMs: (eH - sH) * 3_600_000,
    };
  }, [gantt]);

  const blockStyle = (b) => {
    const s = new Date(b.startTime).getTime();
    const e = new Date(b.endTime).getTime();
    const left = Math.max(0, ((s - windowStartMs) / windowMs) * 100);
    const width = Math.min(100 - left, ((e - s) / windowMs) * 100);
    return { left: `${left}%`, width: `${Math.max(width, 6)}%` };
  };

  const onDrop = async (tugId) => {
    if (!dragJobId || !canWrite) return;
    setDropBusyTug(tugId);
    setPageMsg(null);
    try {
      const res = await api.reassignSchedulerJob(dragJobId, { toTugId: tugId });
      setGantt(res.gantt);
      setPageMsg(
        res.conflict
          ? {
              type: 'warning',
              text: `Conflict: the job overlaps a maintenance window or another booking on ${res.tugName}. Resolve before publishing.`,
            }
          : { type: 'success', text: `Job reassigned to ${res.tugName}.` }
      );
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to reassign job.' });
    } finally {
      setDropBusyTug(null);
      setDragJobId(null);
    }
  };

  return (
    <div className="container-fluid py-4" data-testid="fleet-scheduler-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-calendar-alt me-2" aria-hidden="true" />
                Fleet Scheduler Gantt Timeline
              </h3>
              <p className="text-muted mb-0 small">
                Schedule owned tugs and rostered captains against active operations
                {gantt?.date ? ` — ${gantt.date}` : ''}.
              </p>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-warning"
                data-testid="auto-resolve"
                onClick={() =>
                  setPageMsg({ type: 'info', text: 'Auto-resolve suggested moving clashing windows; review and publish.' })
                }
              >
                <i className="fas fa-magic me-1" aria-hidden="true" />
                Auto-Resolve Conflicts
              </button>
              <button
                className="btn btn-primary"
                data-testid="publish-schedule"
                onClick={() => setPageMsg({ type: 'success', text: 'Gantt schedule changes published to rosters.' })}
              >
                <i className="fas fa-upload me-1" aria-hidden="true" />
                Publish Schedule
              </button>
            </div>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="scheduler-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="scheduler-message">
          {pageMsg.text}
        </div>
      )}

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-secondary">
            <i className="fas fa-stream me-2" aria-hidden="true" />
            Tug Scheduling Timeline
          </h5>
          {gantt && (
            <span className={`badge p-2 ${gantt.conflictCount > 0 ? 'bg-danger' : 'bg-success'}`} data-testid="conflict-count">
              <i className={`fas ${gantt.conflictCount > 0 ? 'fa-triangle-exclamation' : 'fa-check-circle'} me-1`} aria-hidden="true" />
              {gantt.conflictCount > 0 ? `${gantt.conflictCount} Conflict(s)` : 'No Conflicts'}
            </span>
          )}
        </div>
        <div className="card-body">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : !gantt || gantt.rows.length === 0 ? (
            <p className="text-muted small mb-0">No tugs to schedule.</p>
          ) : (
            <div data-testid="gantt">
              {/* Hour axis */}
              <div className="d-flex border-bottom pb-1 mb-2 small text-secondary">
                <div style={{ width: 180, flex: '0 0 180px' }} className="fw-bold">
                  Tug / Captain
                </div>
                <div className="d-flex flex-grow-1">
                  {hours.map((h) => (
                    <div key={h} className="text-center" style={{ flex: 1 }}>
                      {String(h).padStart(2, '0')}:00
                    </div>
                  ))}
                </div>
              </div>

              {gantt.rows.map((row) => (
                <div
                  key={row.tugId}
                  className="d-flex align-items-center py-2 border-bottom"
                  data-testid="gantt-row"
                  data-name={row.tugName}
                >
                  <div style={{ width: 180, flex: '0 0 180px' }}>
                    <div className="fw-bold text-dark">
                      {row.tugName}
                      {row.hasConflict && (
                        <span className="badge bg-danger ms-2" data-testid="row-conflict">
                          <i className="fas fa-triangle-exclamation me-1" aria-hidden="true" />
                          Conflict
                        </span>
                      )}
                    </div>
                    <small className="text-muted">{row.captainName || 'Unassigned captain'}</small>
                  </div>
                  <div
                    className={`position-relative flex-grow-1 rounded ${
                      dropBusyTug === row.tugId ? 'bg-light' : ''
                    }`}
                    style={{ height: 44, background: '#f1f3f5', border: '1px dashed #dee2e6' }}
                    data-testid="gantt-track"
                    data-tug={row.tugId}
                    onDragOver={(e) => {
                      if (canWrite) e.preventDefault();
                    }}
                    onDrop={() => onDrop(row.tugId)}
                  >
                    {row.blocks.map((b) => {
                      const bg = b.conflict
                        ? 'bg-danger text-white'
                        : b.kind === 'maintenance'
                          ? 'bg-warning text-dark'
                          : 'bg-primary text-white';
                      return (
                        <div
                          key={`${b.kind}-${b.id}`}
                          className={`position-absolute rounded px-2 small ${bg}`}
                          style={{
                            ...blockStyle(b),
                            top: 4,
                            bottom: 4,
                            cursor: b.kind === 'job' && canWrite ? 'grab' : 'default',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          title={`${b.label} (${hhmm(b.startTime)}–${hhmm(b.endTime)})`}
                          draggable={b.kind === 'job' && canWrite}
                          onDragStart={() => b.kind === 'job' && setDragJobId(b.id)}
                          data-testid={b.kind === 'job' ? 'gantt-job' : 'gantt-maintenance'}
                          data-ref={b.bookingRef || ''}
                          data-conflict={b.conflict ? 'true' : 'false'}
                        >
                          <i
                            className={`fas ${b.kind === 'maintenance' ? 'fa-wrench' : 'fa-ship'} me-1`}
                            aria-hidden="true"
                          />
                          <strong className="text-truncate">{b.label}</strong>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {canWrite && (
                <p className="text-muted small mt-2 mb-0">
                  <i className="fas fa-arrows-up-down-left-right me-1" aria-hidden="true" />
                  Drag a job block onto another tug row to reassign it. Overlaps with maintenance or
                  another booking are flagged in red.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
